/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IProductCollectionViewProps,
    IProductComponentViewProps
} from '@msdyn365-commerce-modules/product-collection/src/modules/product-collection/./product-collection';
import { IProductCollectionProps, IProductCollectionResources } from '../definition-extensions/product-collection.ext.props.autogenerated';
import { IProductCollectionData } from './product-collection.data';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

const ProductCollectionView: React.FC<IProductCollectionViewProps & IProductCollectionProps<IProductCollectionData>> = props => {
    const {
        heading,
        ProductCollectionContainer,
        products,
        SingleSlideCarouselComponentProps,
        GridComponentProps,
        isCarousel,
        seeAllButton,
        resources
    } = props;
    if (products) {
        const { collectionAttributeRecordId } = props.context.app.config;
        const variantAttributes = props.data.productAttributes.result;
        const selectedVariantCollection =
            variantAttributes && variantAttributes.find((attr: AttributeValue) => attr.RecordId === collectionAttributeRecordId);
        const filteredProducts = products.filter(product => {
            const productEl = product.productComponent as JSX.Element;
            const productData = productEl && productEl.props.data;
            const productAttributes = productData && productData.product.AttributeValues;
            const collectionAttributeValue =
                productAttributes && productAttributes.find((attr: AttributeValue) => attr.RecordId === collectionAttributeRecordId);
            return collectionAttributeValue && collectionAttributeValue.TextValue === selectedVariantCollection?.TextValue;
        });
        if (filteredProducts.length === 0) return null;
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {isCarousel
                    ? _renderCarousel(SingleSlideCarouselComponentProps, filteredProducts)
                    : _renderGrid(GridComponentProps, filteredProducts)}
            </Module>
        );
    }

    // upgrade to 10.0.40
    // If no products are retrieved and the list type is promotional, still render the module to keep the entry of promotion page
    if (props.config.productCollection.listType === 'promotional') {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {_renderNoProduct(resources)}
            </Module>
        );
    }

    props.context.telemetry.error('Product collection content is empty, module wont render.');
    return null;
};

const _renderCarousel = (carouselContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...carouselContainer}>{items && items.map(_renderProduct)}</Node>;
};

const _renderGrid = (gridContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...gridContainer}>{items && items.map(_renderProduct)}</Node>;
};
// upgrade to 10.0.40
const _renderNoProduct = (resources: IProductCollectionResources): JSX.Element => {
    return (
        <Node className={''}>
            <p>{resources.noProductToRenderText}</p>
        </Node>
    );
};

const _renderProduct = (product: IProductComponentViewProps): JSX.Element => {
    const { ProductContainer, productComponent } = product;

    return <Node {...ProductContainer}>{productComponent}</Node>;
};

export default ProductCollectionView;
